import { cn } from '@/lib/utils';
import { useGitStore } from '@/store/gitStore';
import {
    AlertCircle,
    ArrowUpCircle,
    Check,
    ChevronDown,
    ChevronRight,
    FileEdit,
    FilePlus,
    FileX,
    GitBranch,
    GitCommit,
    Loader2,
    Minus,
    Plus,
    RefreshCw,
    X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/* ================================================================== */
/*  File status badge                                                 */
/* ================================================================== */

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; color: string }> = {
    added: { label: 'A', color: 'text-green-400 bg-green-400/10' },
    modified: { label: 'M', color: 'text-yellow-400 bg-yellow-400/10' },
    deleted: { label: 'D', color: 'text-red-400 bg-red-400/10' },
    renamed: { label: 'R', color: 'text-blue-400 bg-blue-400/10' },
    untracked: { label: 'U', color: 'text-gray-400 bg-gray-400/10' },
  };
  const c = config[status] ?? config.modified;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold rounded',
        c.color,
      )}
    >
      {c.label}
    </span>
  );
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'added':
      return <FilePlus className="w-3.5 h-3.5 text-green-400" />;
    case 'deleted':
      return <FileX className="w-3.5 h-3.5 text-red-400" />;
    default:
      return <FileEdit className="w-3.5 h-3.5 text-yellow-400" />;
  }
}

/* ================================================================== */
/*  Branch Selector                                                   */
/* ================================================================== */

function BranchSelector({
  workspaceId,
  current,
  branches,
}: {
  workspaceId: string;
  current: string;
  branches: string[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newBranch, setNewBranch] = useState('');
  const { checkout, createBranch } = useGitStore();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowCreate(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1 bg-[#1e1e1e] rounded text-xs hover:bg-[#2a2d2e] transition-colors w-full"
      >
        <GitBranch className="w-3.5 h-3.5 text-[#858585]" />
        <span className="flex-1 text-left truncate text-[#d4d4d4]">{current}</span>
        <ChevronDown className="w-3 h-3 text-[#666]" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#1e1e1e] border border-[#454545] rounded-lg shadow-xl z-20 overflow-hidden">
          <div className="max-h-40 overflow-y-auto">
            {branches.map((b) => (
              <button
                key={b}
                onClick={() => {
                  if (b !== current) checkout(workspaceId, b);
                  setIsOpen(false);
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[11px] transition-colors',
                  b === current ? 'bg-[#04395e] text-white' : 'text-[#cccccc] hover:bg-[#2a2d2e]',
                )}
              >
                <GitBranch className="w-3 h-3 shrink-0" />
                <span className="truncate">{b}</span>
                {b === current && <Check className="w-3 h-3 ml-auto text-green-400" />}
              </button>
            ))}
          </div>

          <div className="border-t border-[#454545]">
            {showCreate ? (
              <div className="p-2 flex gap-1">
                <input
                  value={newBranch}
                  onChange={(e) => setNewBranch(e.target.value)}
                  placeholder="branch-name"
                  className="flex-1 bg-[#252526] text-xs text-[#d4d4d4] px-2 py-1 rounded border border-[#454545] outline-none focus:border-[#007acc]"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newBranch.trim()) {
                      createBranch(workspaceId, newBranch.trim());
                      setNewBranch('');
                      setShowCreate(false);
                      setIsOpen(false);
                    }
                    if (e.key === 'Escape') {
                      setShowCreate(false);
                      setNewBranch('');
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (newBranch.trim()) {
                      createBranch(workspaceId, newBranch.trim());
                      setNewBranch('');
                      setShowCreate(false);
                      setIsOpen(false);
                    }
                  }}
                  className="px-2 py-1 bg-[#007acc] text-white text-[10px] rounded hover:bg-[#005fa3]"
                >
                  Create
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowCreate(true)}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-[#858585] hover:text-white hover:bg-[#2a2d2e] transition-colors"
              >
                <Plus className="w-3 h-3" />
                <span>Create New Branch</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  GitPanel                                                          */
/* ================================================================== */

interface GitPanelProps {
  workspaceId: string;
}

export default function GitPanel({ workspaceId }: GitPanelProps) {
  const {
    branch,
    branches,
    ahead,
    staged,
    unstaged,
    untracked,
    log,
    isLoading,
    commitMessage,
    error,
    fetchStatus,
    fetchLog,
    fetchBranches,
    stageFiles,
    stageAll,
    unstageFiles,
    commit,
    setCommitMessage,
    clearError,
  } = useGitStore();

  const [showStaged, setShowStaged] = useState(true);
  const [showChanges, setShowChanges] = useState(true);
  const [showLog, setShowLog] = useState(false);

  // Fetch on mount + poll
  useEffect(() => {
    if (!workspaceId) return;
    fetchStatus(workspaceId);
    fetchBranches(workspaceId);
    fetchLog(workspaceId);

    const interval = setInterval(() => {
      fetchStatus(workspaceId);
    }, 10000);

    return () => clearInterval(interval);
  }, [workspaceId, fetchStatus, fetchBranches, fetchLog]);

  const totalChanges = staged.length + unstaged.length + untracked.length;

  return (
    <div className="flex flex-col h-full text-[#d4d4d4]">
      {/* Header actions */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-[#1e1e1e]">
        <button
          onClick={() => fetchStatus(workspaceId)}
          className="p-1 text-[#858585] hover:text-white transition-colors"
          title="Refresh"
        >
          <RefreshCw className={cn('w-3.5 h-3.5', isLoading && 'animate-spin')} />
        </button>

        {ahead > 0 && (
          <div className="flex items-center gap-1 text-[10px] text-green-400">
            <ArrowUpCircle className="w-3 h-3" />
            <span>{ahead}</span>
          </div>
        )}

        <div className="flex-1" />

        {totalChanges > 0 && (
          <span className="text-[10px] text-[#858585]">
            {totalChanges} change{totalChanges !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border-b border-red-500/20">
          <AlertCircle className="w-3 h-3 text-red-400 shrink-0" />
          <span className="text-[10px] text-red-300 flex-1">{error}</span>
          <button onClick={clearError} className="text-red-400 hover:text-red-300">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {/* Branch selector */}
        <div className="px-3 py-2">
          <BranchSelector
            workspaceId={workspaceId}
            current={branch}
            branches={branches.length > 0 ? branches : [branch]}
          />
        </div>

        {/* Commit message + button */}
        <div className="px-3 pb-2">
          <div className="relative">
            <textarea
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              placeholder="Commit message"
              className="w-full bg-[#1e1e1e] text-xs text-[#d4d4d4] placeholder-[#666] px-2.5 py-2 rounded border border-[#454545] outline-none focus:border-[#007acc] resize-none transition-colors"
              rows={2}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  commit(workspaceId);
                }
              }}
            />
          </div>
          <button
            onClick={() => commit(workspaceId)}
            disabled={!commitMessage.trim() || staged.length === 0 || isLoading}
            className={cn(
              'w-full mt-1.5 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors',
              commitMessage.trim() && staged.length > 0 && !isLoading
                ? 'bg-[#007acc] text-white hover:bg-[#005fa3]'
                : 'bg-[#333] text-[#666] cursor-not-allowed',
            )}
          >
            {isLoading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Check className="w-3 h-3" />
            )}
            <span>Commit{staged.length > 0 ? ` (${staged.length})` : ''}</span>
          </button>
          <div className="text-[9px] text-[#666] mt-1 text-center">Ctrl+Enter to commit</div>
        </div>

        {/* Staged changes section */}
        <div className="border-t border-[#1e1e1e]">
          <button
            onClick={() => setShowStaged(!showStaged)}
            className="w-full flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-[#bbbbbb] hover:bg-[#2a2d2e] transition-colors"
          >
            {showStaged ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
            <span>Staged Changes</span>
            {staged.length > 0 && (
              <span className="ml-auto text-[9px] bg-[#007acc] text-white px-1.5 py-px rounded-full">
                {staged.length}
              </span>
            )}
          </button>

          {showStaged && staged.length > 0 && (
            <div className="pb-1">
              {staged.map((file) => (
                <div
                  key={file.path}
                  className="flex items-center gap-1.5 px-3 py-0.5 text-[11px] hover:bg-[#2a2d2e] group"
                >
                  <StatusIcon status={file.status} />
                  <span className="flex-1 truncate">{file.path}</span>
                  <StatusBadge status={file.status} />
                  <button
                    onClick={() => unstageFiles(workspaceId, [file.path])}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-[#333] rounded transition-opacity"
                    title="Unstage"
                  >
                    <Minus className="w-3 h-3 text-[#858585]" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showStaged && staged.length === 0 && (
            <div className="px-3 py-2 text-[10px] text-[#666] italic">No staged changes</div>
          )}
        </div>

        {/* Unstaged / untracked changes section */}
        <div className="border-t border-[#1e1e1e]">
          <button
            onClick={() => setShowChanges(!showChanges)}
            className="w-full flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-[#bbbbbb] hover:bg-[#2a2d2e] transition-colors"
          >
            {showChanges ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
            <span>Changes</span>
            {unstaged.length + untracked.length > 0 && (
              <span className="ml-auto flex items-center gap-1">
                <span className="text-[9px] bg-[#555] text-white px-1.5 py-px rounded-full">
                  {unstaged.length + untracked.length}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    stageAll(workspaceId);
                  }}
                  className="p-0.5 hover:bg-[#333] rounded"
                  title="Stage All"
                >
                  <Plus className="w-3 h-3 text-[#858585] hover:text-white" />
                </button>
              </span>
            )}
          </button>

          {showChanges && (
            <div className="pb-1">
              {unstaged.map((file) => (
                <div
                  key={file.path}
                  className="flex items-center gap-1.5 px-3 py-0.5 text-[11px] hover:bg-[#2a2d2e] group"
                >
                  <StatusIcon status={file.status} />
                  <span className="flex-1 truncate">{file.path}</span>
                  <StatusBadge status={file.status} />
                  <button
                    onClick={() => stageFiles(workspaceId, [file.path])}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-[#333] rounded transition-opacity"
                    title="Stage"
                  >
                    <Plus className="w-3 h-3 text-[#858585]" />
                  </button>
                </div>
              ))}
              {untracked.map((path) => (
                <div
                  key={path}
                  className="flex items-center gap-1.5 px-3 py-0.5 text-[11px] hover:bg-[#2a2d2e] group"
                >
                  <FilePlus className="w-3.5 h-3.5 text-green-400" />
                  <span className="flex-1 truncate">{path}</span>
                  <StatusBadge status="untracked" />
                  <button
                    onClick={() => stageFiles(workspaceId, [path])}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-[#333] rounded transition-opacity"
                    title="Stage"
                  >
                    <Plus className="w-3 h-3 text-[#858585]" />
                  </button>
                </div>
              ))}
              {unstaged.length === 0 && untracked.length === 0 && (
                <div className="px-3 py-2 text-[10px] text-[#666] italic">No changes</div>
              )}
            </div>
          )}
        </div>

        {/* Commit log section */}
        <div className="border-t border-[#1e1e1e]">
          <button
            onClick={() => {
              setShowLog(!showLog);
              if (!showLog && log.length === 0) fetchLog(workspaceId);
            }}
            className="w-full flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-[#bbbbbb] hover:bg-[#2a2d2e] transition-colors"
          >
            {showLog ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <span>Commit History</span>
          </button>

          {showLog && (
            <div className="pb-1">
              {log.length === 0 ? (
                <div className="px-3 py-2 text-[10px] text-[#666] italic">No commits yet</div>
              ) : (
                log.map((entry) => (
                  <div
                    key={entry.hash}
                    className="px-3 py-1.5 hover:bg-[#2a2d2e] transition-colors"
                  >
                    <div className="flex items-center gap-1.5">
                      <GitCommit className="w-3 h-3 text-[#858585] shrink-0" />
                      <span className="text-[11px] truncate flex-1">{entry.message}</span>
                      <span className="text-[9px] text-[#666] font-mono shrink-0">
                        {entry.shortHash}
                      </span>
                    </div>
                    <div className="ml-[18px] text-[9px] text-[#666] mt-0.5">
                      {entry.author} · {formatDate(entry.date)}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Helpers                                                           */
/* ================================================================== */

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  } catch {
    return dateStr;
  }
}
